import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Country } from "@shared/types/commonTypes";

// Export query key constant for reference
export const COUNTRIES_QUERY_KEY = ["countries"] as const;

type UseCountriesResult = {
  countries: Country[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch and cache countries data.
 * Countries are static metadata that rarely change, so they are cached indefinitely.
 * 
 * Note: This is in-memory cache (per session). Data is cleared on page refresh.
 * For persistent cache across page loads, consider adding React Query persistence.
 */
export function useCountries(): UseCountriesResult {
  const { GET } = useApiRequest();
  const {
    data: countries = [],
    isLoading,
    error,
  } = useQuery<Country[]>({
    queryKey: COUNTRIES_QUERY_KEY,
    queryFn: () => GET("/api/metadata/countries"),
    // Countries are static metadata - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  return {
    countries,
    isLoading,
    error: error ?? null,
  } satisfies UseCountriesResult;
}
